import { Slider, Grid, FormControl, Typography } from '@mui/material';
import { FC, SetStateAction, Dispatch } from 'react';
import {
  FiltersLayout,
  FiltersButtons,
  SearchField,
  NeighborhoodDropdown,
  VendorDropdown,
  VendorContactDropdown,
  DateRangePicker,
  PunchlistTypeDropdown,
  InternalUsersDropdown,
  NeighborhoodLotsDropdown
} from '../../../components';
import { PunchlistItemAssignedToSlim } from '../punchlists/assign-to-dropdown-slim';
import { PunchlistTerritoryFilter } from './PunchlistTerritoryFilter';
import {
  IDateRange,
  IVendorContact,
  INeighborhood,
  IDomainUser,
  IDropdownResponse
} from '../../../models';

interface IPunchlistFilters {
  isLoading: boolean;
  applyFilters: (clearFilters?: boolean) => void;
  setSelectedTerritoryId?: (val: string | number) => void;
  selectedTerritoryId?: string | number;
  setHasAppliedFilters: Dispatch<SetStateAction<boolean>>;
  hasAppliedFilters: boolean;
  setSearchValue?: (val: string) => void;
  setSearchedValue?: (val: string) => void;
  searchValue?: string;
  handleSearch?: (val: string) => void;
  setSelectedNeighborhoodId?: (val: string | number) => void;
  selectedNeighborhoodId?: string | number;
  selectedLotNumber?: string;
  setSelectedLotNumber?: (val: any) => void;
  selectedDateRange?: IDateRange | null;
  setSelectedDateRange?: Dispatch<SetStateAction<IDateRange>>;
  setSelectedVendorId?: (val: string | number) => void;
  selectedVendorId?: string | number;
  setVendors?: Dispatch<SetStateAction<IDropdownResponse[]>>;
  setNeighborhoods?: Dispatch<SetStateAction<INeighborhood[]>>;
  setVendorContacts?: Dispatch<SetStateAction<IVendorContact[]>>;
  setVendorContact?: (val: string) => void;
  vendorContact?: string;
  searchPlaceholder?: string;
  selectedCount?: number;
  setSelectedCount?: Dispatch<SetStateAction<number>>;
  hideVendorFilter?: boolean;
  selectedType?: string | number;
  setSelectedType?: (val: string | number) => void;
  selectedAssignee?: IDomainUser | null;
  setSelectedAssignee?: (val: IDomainUser | null) => void;
  setAssigneeOptions?: (val: IDomainUser[]) => void;
  handleClearSearch?: () => void;
  selectedAssigneedTo?: string;
  setSelectedAssigneedTo?: (val: string) => void;
  setShowAssignedToFilter?: Dispatch<SetStateAction<boolean>>;
  showAssignedToFilter?: boolean;
}

// NOTE: setSelectedAssigneedTo, selectedAssigneedTo, setShowAssignedToFilter refer to the filter for the ASSIGNED TO dropdown.
// The state for selectedAssignee and setSelectedAssignee are not releated - they are used for INTERNAL USER dropdown.

export const PunchlistFilters: FC<IPunchlistFilters> = ({
  isLoading,
  applyFilters,
  selectedTerritoryId,
  setSelectedTerritoryId,
  setHasAppliedFilters,
  hasAppliedFilters,
  handleSearch,
  searchValue,
  setSearchValue,
  setSearchedValue,
  setSelectedVendorId,
  selectedVendorId,
  selectedNeighborhoodId,
  setSelectedNeighborhoodId,
  selectedLotNumber,
  setSelectedLotNumber,
  selectedDateRange,
  setSelectedDateRange,
  setVendors,
  setNeighborhoods,
  setVendorContacts,
  setVendorContact,
  vendorContact,
  searchPlaceholder,
  setSelectedCount,
  selectedCount,
  hideVendorFilter = false,
  setSelectedType,
  selectedType,
  selectedAssignee,
  setSelectedAssignee,
  setAssigneeOptions,
  handleClearSearch,
  setSelectedAssigneedTo,
  selectedAssigneedTo,
  setShowAssignedToFilter,
  showAssignedToFilter = false
}) => {
  return (
    <FiltersLayout>
      {handleSearch && (
        <Grid item xs={12} sm={6} md={4} xl={3}>
          <SearchField
            isDisabled={isLoading}
            placeholder={searchPlaceholder}
            handleSearch={handleSearch}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            handleClearSearch={handleClearSearch}
          />
        </Grid>
      )}

      {setSelectedTerritoryId && (
        <Grid item xs={12} sm={6} md={4} xl={3}>
          <PunchlistTerritoryFilter
            isDisabled={isLoading || (selectedNeighborhoodId && !selectedTerritoryId)}
            value={selectedTerritoryId}
            handleChange={value => {
              setSelectedTerritoryId(value);
            }}
          />
        </Grid>
      )}
      {setSelectedNeighborhoodId && (
        <Grid item xs={12} sm={6} md={4} xl={3}>
          <NeighborhoodDropdown
            isDisabled={isLoading}
            value={selectedNeighborhoodId}
            handleChange={value => {
              setSelectedNeighborhoodId(value);
            }}
            apiParams={{ hasPunchlists: true, territoryId: selectedTerritoryId }}
            setOptions={options => setNeighborhoods?.(options)}
          />
        </Grid>
      )}
      {setSelectedLotNumber && (
        <Grid item xs={12} sm={6} md={4} xl={3}>
          <NeighborhoodLotsDropdown
            value={selectedLotNumber}
            isDisabled={!selectedNeighborhoodId}
            handleChange={value => setSelectedLotNumber(value)}
            apiParams={{ neighborhoodId: selectedNeighborhoodId, includeGeneralJob: true }}
          />
        </Grid>
      )}
      {setSelectedVendorId && (
        <Grid
          item
          xs={12}
          sm={6}
          md={4}
          xl={3}
          sx={{ display: { xs: hideVendorFilter ? 'none' : 'flex' } }}
        >
          <VendorDropdown
            isDisabled={isLoading}
            value={selectedVendorId}
            handleChange={value => {
              setSelectedVendorId(value);
            }}
            hasPunchLists={!hideVendorFilter}
            setOptions={options => setVendors?.(options)}
          />
        </Grid>
      )}
      {selectedVendorId && setVendorContact && (
        <Grid item xs={12} sm={6} md={4} xl={3}>
          <VendorContactDropdown
            isDisabled={isLoading}
            value={vendorContact}
            handleChange={value => {
              setVendorContact(value);
            }}
            selectedVendorId={selectedVendorId}
            label="Vendor Contact"
            setOptions={options => setVendorContacts?.(options)}
            apiParams={{ isActive: true }}
          />
        </Grid>
      )}
      {setSelectedAssignee && (
        <Grid item xs={12} sm={6} md={4} xl={3}>
          <InternalUsersDropdown
            selectedAssignee={selectedAssignee}
            setSelectedAssignee={setSelectedAssignee}
            setOptions={options => setAssigneeOptions(options)}
          />
        </Grid>
      )}
      {setSelectedDateRange && (
        <Grid item xs={12} sm={6} md={4} xl={3}>
          <FormControl fullWidth variant="outlined" size="small">
            <DateRangePicker
              id="filterDateRange"
              label="Date Range"
              value={selectedDateRange}
              isDisabled={isLoading}
              onChange={value => {
                setSelectedDateRange(value);
              }}
              fullWidth
              inputSize="small"
            />
          </FormControl>
        </Grid>
      )}
      {setSelectedType && (
        <Grid item xs={12} sm={6} md={4} xl={3}>
          <PunchlistTypeDropdown value={selectedType} handleChange={val => setSelectedType(val)} />
        </Grid>
      )}
      {setSelectedCount && (
        <Grid item xs={12} sm={6} md={4} xl={3}>
          <Typography>Item Count Greater Than</Typography>
          <Slider
            value={selectedCount}
            onChange={(_, val) => setSelectedCount(val as number)}
            valueLabelDisplay="auto"
          />
        </Grid>
      )}
      {/* Conditionally render the assigned to filter */}
      {setShowAssignedToFilter && showAssignedToFilter && (
        <Grid item xs={12} sm={6} md={4} xl={3}>
          <FormControl fullWidth variant="outlined" size="small" required>
            <PunchlistItemAssignedToSlim
              value={selectedAssigneedTo}
              handleChange={value => setSelectedAssigneedTo(value)}
            />
          </FormControl>
        </Grid>
      )}
      <Grid
        item
        xs={12}
        md={5}
        xl={setShowAssignedToFilter && showAssignedToFilter && hasAppliedFilters ? 12 : 3}
      >
        <FiltersButtons
          hasAppliedFilters={hasAppliedFilters}
          isDisabled={isLoading}
          handleApplyFilters={() => {
            setHasAppliedFilters(true);
            applyFilters();
          }}
          handleResetFilters={() => {
            setHasAppliedFilters(false);
            setSelectedTerritoryId && setSelectedTerritoryId('');
            setSelectedNeighborhoodId && setSelectedNeighborhoodId('');
            setSelectedLotNumber?.('');
            setSelectedVendorId && setSelectedVendorId('');
            setVendorContact && setVendorContact('');
            setSelectedAssignee && setSelectedAssignee(null);
            setSelectedCount && setSelectedCount(1);
            setSelectedType && setSelectedType('');
            setSelectedAssigneedTo && setSelectedAssigneedTo('');
            setSelectedDateRange &&
              setSelectedDateRange({
                startDate: null,
                endDate: null,
                key: 'selection',
                inputValue: ''
              });
            setSearchedValue && setSearchedValue('');
            setSearchValue && setSearchValue('');
            applyFilters(true);
          }}
        />
      </Grid>
    </FiltersLayout>
  );
};
